import { HashMap, Identificable } from 'shared/types/commonView';

export function isEmptyArray(value: unknown): value is [] {
  return Array.isArray(value) && value.length === 0;
}

export function isNonEmptyArray<T>(value: unknown): value is T[] {
  return Array.isArray(value) && value.length > 0;
}

export function hasAtLeastItems(
  value: unknown,
  minimumItems: number
): value is unknown[] {
  return Array.isArray(value) && value.length >= minimumItems;
}

export function hasAtLeastDefinedItems<T>(
  value: unknown,
  minimumItems: number
): value is NonNullable<T>[] {
  return (
    Array.isArray(value)
    && value.length >= minimumItems
    && value.every((item) => item != null)
  );
}

export function toArrayValue<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

/**
 * Ensures the input is always an array.
 * - undefined/null → empty array
 * - array → returns as-is
 * - single item → wraps in array
 *
 * Use when accepting flexible input that may be single item or array (e.g. legacy API).
 */
export function ensureArray<T>(
  value: readonly T[] | T | undefined | null
): readonly T[] {
  if (value == null) return [];
  if (Array.isArray(value)) return value;
  return [value] as readonly T[];
}

export function pickFirstNonEmptyString(
  candidates: Array<string | undefined | null>,
  fallback = ''
): string {
  for (const candidate of candidates) {
    const trimmed = String(candidate ?? '').trim();
    if (!trimmed) continue;
    return trimmed;
  }
  return fallback;
}

export const findInsertionIndex = (
  orderedArray: string[],
  toInsert: string
): number => {
  let index = 0;

  orderedArray.forEach((el) => {
    if (el.localeCompare(toInsert) >= 0) {
      return index;
    }

    index += 1;
  });

  return index;
};

export const insertAt = <T>(
  array: T[],
  index: number,
  element: T
): void => {
  array.splice(index, 0, element);
};

export const transformArrayToHashMap = <T extends Identificable>(
  list: T[]
): HashMap<T> =>
  list.reduce((result: HashMap<T>, currentValue: T) => {
    result[currentValue._id] = currentValue;
    return result;
  }, {});

export async function asyncSome<T>(
  array: T[],
  predicate: (item: T) => Promise<boolean>
): Promise<boolean> {
  for (const item of array) {
    if (await predicate(item)) {
      return true;
    }
  }
  return false;
}

export async function asyncMap<T, R>(
  array: T[],
  mapper: (item: T, index: number) => Promise<R>
): Promise<R[]> {
  const results: R[] = [];
  for (let i = 0; i < array.length; i++) {
    results.push(await mapper(array[i], i));
  }
  return results;
}

export function areArraysShallowEqual<T>(
  first: T[],
  second: T[]
): boolean {
  if (first.length !== second.length) {
    return false;
  }

  for (let index = 0; index < first.length; index += 1) {
    if (first[index] !== second[index]) {
      return false;
    }
  }

  return true;
}

/**
 * Compares two arrays of Identificable items by their IDs.
 * Returns true if both arrays contain the same IDs (order-independent).
 * 
 * @param prev - Previous array of items
 * @param current - Current array of items
 * @returns true if arrays have same IDs, false otherwise
 */
export function areItemIdsEqual<T extends Identificable>(
  prev: T[],
  current: T[]
): boolean {
  if (prev.length !== current.length) return false;
  if (prev.length === 0) return true;
  
  const prevIds = prev.map(item => item._id).sort().join(',');
  const currentIds = current.map(item => item._id).sort().join(',');
  
  return prevIds === currentIds;
}
